import React, { useEffect, useState } from 'react';

import { useLocation } from 'react-router-dom';
import Skeleton from '@mui/material/Skeleton';
import { useAbility } from '@casl/react';
import classNames from 'classnames';

import { Box } from '@vk-hr-tek/ui/Box';
import { Typography } from '@vk-hr-tek/ui/Typography';
import { Divider } from '@vk-hr-tek/ui/Divider';
import { Tooltip } from '@vk-hr-tek/ui/Tooltip';
import { Icon } from '@vk-hr-tek/ui/Icon';

import { UserRoleEnum } from '@app/types';
import { NavigationItem, NavigationExternalItem } from '@app/router';
import { AppAbilities, Resources } from '@app/ability/ability';
import { AbilityContext } from '@app/ability/Can';
import { useIsWidget, useSelector } from '@app/hooks';

import { transformItemsToNavigation } from '../helpers/transformItemsToNavigation';
import { selectUser } from '../../../../user';
import { ITEM_HEIGHT } from '../Sidebar.constants';
import { MenuItem, sidebarItemMapper } from '../helpers';

import { useStyles } from './SidebarMenu.styles';

interface SidebarMenuProps {
  sidebarOpened: boolean;
  skeletonLoading: boolean;
  menuLabel: string;
  maxItemsInMenu?: number;
  onMenuItemClick: (pageTitle: string) => void;
  externalItems?: NavigationExternalItem[];
  items: MenuItem[];
}

export const SidebarMenu = ({
  sidebarOpened,
  skeletonLoading,
  menuLabel,
  maxItemsInMenu = 5,
  onMenuItemClick,
  externalItems = [],
  items,
}: SidebarMenuProps) => {
  const { pathname } = useLocation();
  const ability = useAbility(AbilityContext);
  const user = useSelector(selectUser);
  const isWidget = useIsWidget();
  const userId = user?.id || '';

  const formatedItems: NavigationItem[] = transformItemsToNavigation(items);

  const handleIsActive = (path: string, href: string): boolean => {
    const userRole = href.split('/')[1] || UserRoleEnum.Employee;

    if (href.startsWith('/personal')) {
      const currentUserId = pathname.split('/personal/')[1] || '';
      return path.startsWith('/personal') && userId === currentUserId;
    }

    if (href === `/${userRole}/events`) {
      return (
        path === `/${userRole}/events` ||
        (path.startsWith(`/${userRole}/events/`) && !path.includes('/paper'))
      );
    }

    if (href === `/${userRole}/settings/attorneys`) {
      return (
        path === `/${userRole}/settings/attorneys` ||
        (path.startsWith(`/${userRole}/settings/attorneys/`) &&
          !path.includes('/attorneys-users'))
      );
    }

    return path === href || path.startsWith(`${href}/`);
  };

  const createLinkHref = (href: string, isCustom = false): string => {
    if (isCustom) {
      return href;
    }

    if (href === '/personal') {
      return `/personal/${userId}`;
    }
    return href;
  };

  const urlWithPrefix = (url: string) => {
    if (isWidget) {
      return `${window.REACT_APP_VKHRTEK_WIDGET_STATIC_URL}${url}`;
    }
    return url;
  };

  const filteredLinks = formatedItems
    .filter((item) => ability.can('read', item.resource as AppAbilities[1]))
    .map((item) => {
      const isCustom = !!item.title;
      const newHref = createLinkHref(item.href, isCustom);
      return {
        ...item,
        href: urlWithPrefix(newHref),
        submenu: item.submenu?.map((el) => {
          const isSubItemCustom = !!el.title;
          return {
            ...el,
            href: urlWithPrefix(createLinkHref(el.href, isSubItemCustom)),
            isCustom: isSubItemCustom,
          };
        }),
        isCustom,
        isActive: handleIsActive(pathname, newHref),
      };
    });

  const links: (NavigationItem & {
    isActive: boolean;
    title?: string;
    isCustom?: boolean;
  })[] = [
    ...filteredLinks,
    ...externalItems?.map((item) => ({
      ...item,
      icon: item.icon || (
        <Icon name="home_outline" size="20" color="text.light.primary" />
      ),
      resource: 'all' as Resources,
      aqaClass: 'aqa_external',
      isCustom: true,
      submenu: item.submenu?.map((subitem) => ({
        ...subitem,
        resource: 'all' as Resources,
        aqaClass: 'aqa_external_sub',
        isCustom: true,
      })),
      isActive: false,
    })),
  ];

  const [allElementsVisible, setAllElementsVisible] = useState<boolean>(false);
  const [submenusVisibleItemsCount, setSubmenusVisibleItemsCount] =
    useState<number>(0);

  const handleSetSubmenuItemsCount = (count: number) => {
    setSubmenusVisibleItemsCount((prev) => prev + count);
  };

  // eslint-disable-next-line no-magic-numbers
  const roleTitleFontSize = sidebarOpened ? 14 : 1;

  const itemsInMenu =
    links.length > maxItemsInMenu ? maxItemsInMenu : links.length;

  const menuHeight =
    ((allElementsVisible ? links.length : itemsInMenu) +
      submenusVisibleItemsCount) *
    ITEM_HEIGHT;

  const { classes } = useStyles({
    menuHeight,
    sidebarOpened,
    roleTitleFontSize,
    allElementsVisible,
  });

  const handleAllElementsVisibleState = () => {
    setAllElementsVisible((prev) => !prev);
  };

  const toggleButtonText = allElementsVisible
    ? 'Показать меньше'
    : `Показать еще ${links.length - maxItemsInMenu}`;

  const additionalOptions = externalItems.reduce((acc, item) => {
    const role = item.title?.startsWith('company')
      ? UserRoleEnum.Company
      : UserRoleEnum.Employee;

    if (!acc[role]) {
      acc[role] = [];
    }
    acc[role].push(item);
    return acc;
  }, {} as Record<UserRoleEnum, NavigationExternalItem[]>);

  useEffect(() => {
    if (!sidebarOpened) {
      setSubmenusVisibleItemsCount(0);
    }
  }, [sidebarOpened]);

  return (
    <Box display="flex" flexDirection="column" gap="4">
      <Box className={classes.sidebarRoleTitle}>
        {sidebarOpened ? (
          <Typography variant="inherit" noWrap color="text.light.tertirary">
            {skeletonLoading ? <Skeleton width={200} /> : menuLabel}
          </Typography>
        ) : (
          <Box width="100%">
            <Divider />
          </Box>
        )}
      </Box>

      <Box className={classes.sidebarLinks}>
        {items.map((item) => {
          const mapperProps = {
            item,
            sidebarOpened,
            isSkeletonLoading: skeletonLoading,
            isActive: handleIsActive,
            pathname,
            additionalOptions,
            onMenuItemClick,
            setSubmenusVisibleItemsCount: handleSetSubmenuItemsCount,
          };

          return sidebarItemMapper[item.type](mapperProps);
        })}
      </Box>

      {links.length > maxItemsInMenu && (
        <Box
          className={classNames(classes.showAllButton, 'aqa_showAllButton')}
          onClick={handleAllElementsVisibleState}
        >
          <Tooltip
            title={toggleButtonText}
            placement="right"
            disabled={sidebarOpened}
          >
            <Box
              p="8"
              display="flex"
              minWidth={40}
              minHeight={40}
              alignItems="center"
              justifyContent="center"
              fontSize="20px"
              className={classes.expandMoreIcon}
            >
              {skeletonLoading ? (
                <Skeleton width="100%" />
              ) : (
                <Icon
                  name="chevron_down"
                  size="20"
                  color="original.brand.primary"
                />
              )}
            </Box>
          </Tooltip>

          <Box className={classes.showAllButtonText}>
            <Typography variant="body3" color="text.light.tertirary" noWrap>
              {toggleButtonText}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};
